/* eslint-disable no-nested-ternary */
import React, { useCallback, useState, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { color, layout, space, typography } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Input } from 'antd';
import selectFilters from 'redux-api/reselect/filters';
import { useSelector, useDispatch } from 'react-redux';
import { getWildcardRequest } from 'redux-api/action/wildcard';
import wildcardSelector from 'redux-api/reselect/wildcard';
import { useOutsideClick } from 'hooks';
import CheckCircle from 'theme/icons/check-circle';
import Alert from 'theme/icons/alert';
import Search from 'theme/icons/search';
import Loader from 'theme/icons/loader';
import List from './list';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Wrapper = styled.div`
  ${layout}
  ${space}
  ${typography}

  position: relative;

  outline: 0;

  .ant-input {
    appearance: none;
    margin: 0;
    font-variant: tabular-nums;
    list-style: none;
    font-feature-settings: 'tnum';
    position: relative;
    display: inline-block;
    color: ${themeGet('colors.grey_400')};
    font-size: 16px;
    line-height: 1.5715;
    background-color: ${themeGet('colors.white')};
    background-image: none;
    box-shadow: none;
    transition: all 0.3s;
    width: 100%;
    height: 38px;
    border: 2px solid;
    border-color: ${({ meta }) =>
      meta.error && meta.touched ? themeGet('colors.red_400') : themeGet('colors.primary_100')};
    box-sizing: border-box;
    border-radius: 4px;
    padding: 0 ${themeGet('space.s')}px;
    padding-right: ${({ addonAfter }) =>
      addonAfter ? themeGet('space.l') : themeGet('space.s')}px;

    :hover {
      border-color: ${themeGet('colors.primary_200')};
    }

    :focus {
      border-color: ${themeGet('colors.primary_500')};
    }

    :disabled {
      opacity: 0.5;
    }

    :placeholder-shown {
      text-overflow: ellipsis;
    }
  }

  .ant-input-affix-wrapper,
  .ant-input-group {
    position: relative;
  }

  .ant-input-suffix {
    position: absolute;
    right: ${themeGet('space.s')}px;
    top: 0px;
    bottom: 0px;

    .ant-input-password-icon {
      color: ${themeGet('colors.grey_600')};

      & > svg {
        width: 18px;
        height: 18px;
      }
    }
  }

  .ant-input-group-addon {
    position: absolute;
    right: ${themeGet('space.s')}px;
    top: 0px;
    bottom: 0px;
    color: ${({ meta }) =>
      !meta.touched
        ? themeGet('colors.grey_600')
        : meta.error
        ? themeGet('colors.red_400')
        : themeGet('colors.primary_400')};
  }
`;

const LabelColor = styled.div`
  ${color}

  position: absolute;
  top: 2px;
  left: 2px;
  width: 10px;
  height: 34px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
`;

const WrapIcon = styled.div`
  ${color}

  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WrapLoader = styled.div`
  width: 18px;
  height: 18px;
  animation: ${rotate} 2s linear infinite;
`;

function CustomInput({ addonAfter, disabled, meta, placeholder, style, data, onSelect }) {
  const ref = useRef();
  const dispatch = useDispatch();

  const [value, setValue] = useState('');
  const [offset, setOffset] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const { dateTo, dateFrom } = useSelector(selectFilters);

  const { isWaiting, payload } = useSelector((state) => wildcardSelector(state, data.type));

  const paginatedResult = useMemo(() => payload.slice(0, 10 + offset), [offset, payload]);

  const haveMore = useMemo(() => {
    const limit = 10 + offset;

    return payload.length > limit;
  }, [offset, payload]);

  const getWildcards = useCallback(
    (v) => {
      dispatch(
        getWildcardRequest({
          offset: 0,
          term: data.type,
          wildcard: v,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateFrom, dateTo],
  );

  const handleChange = useCallback((e) => {
    const { value: v } = e.target;
    setValue(v);
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const { value: v } = e.target;

      if (!v) return;

      onSelect({ type: data.type, value: `${data.type}=${v}`, label: v, color: data.color }, false);
      setIsOpen(false);
      setOffset(0);
      setValue('');
    },
    [onSelect, data],
  );

  const handleExclude = useCallback((label) => {
    onSelect({ type: data.type, value: `${data.type}=${label}`, label, color: data.color }, true);
    setIsOpen(false);
    setOffset(0);
    setValue('');
  }, [onSelect, data]);

  const handleMore = useCallback(() => {
    const newOffset = offset + 10;
    setOffset(newOffset);

    getWildcards(value, newOffset);
  }, [offset, value, getWildcards]);

  useOutsideClick(ref, () => {
    setIsOpen(false);
  });

  const addonAfterIcon = useMemo(() => {
    if (!addonAfter) return '';

    return meta.error && meta.touched ? <Alert /> : <CheckCircle />;
  }, [addonAfter, meta]);

  return (
    <Wrapper
      addonAfter={addonAfter}
      fontSize="display"
      lineHeight="display"
      fontWeight="normal"
      meta={meta}
      {...style}
      ref={ref}
    >
      <Input
        id={data.type}
        value={value}
        onPressEnter={handleSubmit}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        addonAfter={addonAfterIcon}
      />
      <LabelColor bg={data.color || 'black'} />
      <WrapIcon color="primary_200">
        {isWaiting ? (
          <WrapLoader>
            <Loader width={18} />
          </WrapLoader>
        ) : (
          <Search />
        )}
      </WrapIcon>

      <List haveMore={haveMore} options={paginatedResult} isOpen={isOpen} onMore={handleMore} onExclude={handleExclude} />
    </Wrapper>
  );
}

export default CustomInput;
